// A strategy template is a declarative, instrument-independent specification of a multi-leg position,
// expressed as offsets from the at-the-money strike.
export const STRATEGY_BIASES = ['NEUTRAL', 'BULLISH', 'BEARISH', 'VOLATILITY', 'SPECIAL'];
/** Every variant's tag, for a form that offers them all. */
export const STRIKE_CRITERIA_KINDS = [
    'ATM_OFFSET',
    'CLOSEST_PREMIUM',
    'PREMIUM_RANGE',
    'CLOSEST_DELTA',
    'DELTA_RANGE',
];
/**
 * The most legs a strategy may be SAVED with — ROADMAP 5.23.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CEILING ON GROWTH, NOT ON WHAT EXISTS
 *
 * Seven covers every structure in this catalogue with room over: the widest
 * is four legs, and a butterfly with both wings broken is six. Past that a
 * basket stops being a structure a trader reasons about and becomes a list
 * they maintain — and every leg is another order to place, another fill to
 * reconcile and another thing to be half-done when one is rejected.
 *
 * It is enforced on the SAVE and compared against what the strategy already
 * has, so a row that predates the rule — the thirty-leg speed test — stays
 * readable, stays editable and stays firable. It simply may not grow. A cap
 * applied to existing rows would make that strategy unopenable in the only
 * screen that could shrink it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const MAX_STRATEGY_LEGS = 7;
/**
 * What a strategy a trader BUILT is filed under, rather than seeded from.
 *
 * `Strategy.templateKey` is unique per user and is what makes seeding
 * idempotent (5.18): a second run collides instead of duplicating. A built
 * strategy has no template, so it gets a key of its own that can never
 * collide with a catalogue entry — and `isCustomTemplate` is how the seeder
 * and the delete rule tell the two apart without a column.
 */
export const CUSTOM_TEMPLATE_PREFIX = 'CUSTOM:';
export function isCustomTemplate(templateKey) {
    return templateKey.startsWith(CUSTOM_TEMPLATE_PREFIX);
}
/** Shorthand so the catalogue below stays readable. */
const atm = (steps) => ({ kind: 'ATM_OFFSET', steps });
export const STRATEGY_TEMPLATES = {
    SHORT_STRADDLE: {
        key: 'SHORT_STRADDLE',
        name: 'Short Straddle',
        bias: 'NEUTRAL',
        construction: 'Sell ATM CE + PE',
        context: 'Sideways, decay-heavy day',
        legs: [
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(0), ratio: 1 },
        ],
    },
    SHORT_STRANGLE: {
        key: 'SHORT_STRANGLE',
        name: 'Short Strangle',
        bias: 'NEUTRAL',
        construction: 'Sell OTM CE + Sell OTM PE',
        context: 'Range-bound market, low realized volatility, time decay',
        legs: [
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(1), ratio: 1 },
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(-1), ratio: 1 },
        ],
    },
    LONG_STRADDLE: {
        key: 'LONG_STRADDLE',
        name: 'Long Straddle',
        bias: 'VOLATILITY',
        construction: 'Buy ATM CE + Buy ATM PE',
        context: 'Expecting a large price move in either direction',
        legs: [
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(0), ratio: 1 },
        ],
    },
    LONG_STRANGLE: {
        key: 'LONG_STRANGLE',
        name: 'Long Strangle',
        bias: 'VOLATILITY',
        construction: 'Buy OTM CE + Buy OTM PE',
        context: 'Expecting a significant move beyond the breakeven levels',
        legs: [
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(1), ratio: 1 },
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(-1), ratio: 1 },
        ],
    },
    BULL_CALL_SPREAD: {
        key: 'BULL_CALL_SPREAD',
        name: 'Bull Call Spread',
        bias: 'BULLISH',
        construction: 'Buy ATM CE + Sell higher-strike CE',
        context: 'Moderately bullish market, limited risk and reward',
        legs: [
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(1), ratio: 1 },
        ],
    },
    BULL_PUT_SPREAD: {
        key: 'BULL_PUT_SPREAD',
        name: 'Bull Put Spread',
        bias: 'BULLISH',
        construction: 'Sell ATM PE + Buy lower-strike PE',
        context: 'Moderately bullish or stable market, net credit strategy',
        legs: [
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(-1), ratio: 1 },
        ],
    },
    BEAR_CALL_SPREAD: {
        key: 'BEAR_CALL_SPREAD',
        name: 'Bear Call Spread',
        bias: 'BEARISH',
        construction: 'Sell ATM CE + Buy higher-strike CE',
        context: 'Moderately bearish or stable market, net credit strategy',
        legs: [
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(1), ratio: 1 },
        ],
    },
    BEAR_PUT_SPREAD: {
        key: 'BEAR_PUT_SPREAD',
        name: 'Bear Put Spread',
        bias: 'BEARISH',
        construction: 'Buy ATM PE + Sell lower-strike PE',
        context: 'Moderately bearish market, limited risk and reward',
        legs: [
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(-1), ratio: 1 },
        ],
    },
    IRON_FLY: {
        key: 'IRON_FLY',
        name: 'Iron Fly',
        bias: 'NEUTRAL',
        construction: 'Sell ATM CE + PE, Buy OTM CE + PE',
        context: 'Range-bound market, expecting expiry near ATM, defined risk',
        legs: [
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(1), ratio: 1 },
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(-1), ratio: 1 },
        ],
    },
    IRON_CONDOR: {
        key: 'IRON_CONDOR',
        name: 'Iron Condor',
        bias: 'NEUTRAL',
        construction: 'Strangle with wings',
        context: 'Defined-risk range play',
        /*
         * A short strangle with both sides capped. The shorts define where the
         * profit stops; the longs are what make the loss finite, and buying them
         * is not optional — without a wing this is a naked short.
         *
         * Wing width is TWO steps, while IRON_FLY above uses one. That is a
         * deliberate asymmetry in the defaults rather than an oversight: a fly is
         * sold at the money where premium is richest and a narrow wing still pays
         * for itself, whereas a condor is sold further out and a one-step wing
         * would collect almost nothing for the margin it ties up.
         */
        legs: [
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(2), ratio: 1 },
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(-2), ratio: 1 },
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(4), ratio: 1 },
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(-4), ratio: 1 },
        ],
    },
    BATMAN: {
        key: 'BATMAN',
        name: 'Batman',
        bias: 'SPECIAL',
        construction: 'Straddle with double wings — the bat ears',
        context: 'Sell the body, keep the tails open',
        /*
         * A short straddle for the body, with DOUBLE-size long wings — the ratio
         * 2 is literally what "double wings" names, and it is what lifts the far
         * tails back up into the two ears the payoff is named for.
         *
         * Because the wings outnumber the shorts two to one, the extremes are net
         * LONG: this is the rare short-premium structure whose tail risk is
         * bounded. The money is lost in the two valleys between the body and the
         * ears, not at the edges.
         */
        legs: [
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(2), ratio: 2 },
            { side: 'BUY', optionType: 'PE', strikeCriteria: atm(-2), ratio: 2 },
        ],
    },
    JADE_LIZARD: {
        key: 'JADE_LIZARD',
        name: 'Jade Lizard',
        bias: 'SPECIAL',
        construction: 'Short put + call spread',
        context: 'Zero upside risk · Bullish bias',
        /*
         * A short put plus a short call spread. "Zero upside risk" is a CONDITION,
         * not a property of the shape: it holds only while the total credit taken
         * in is at least the width of the call spread, which is two steps here.
         * If the credit comes in thin, the upside is capped-but-loss-making and
         * the name stops being true — a check that belongs in the 5.8 pre-flight,
         * where the live premium is known. Nothing in a template can know it.
         *
         * The DOWNSIDE is uncapped. The short put has no long put beneath it, so
         * this carries the same tail as a naked short put and must never be
         * described to the user as defined-risk.
         */
        legs: [
            { side: 'SELL', optionType: 'PE', strikeCriteria: atm(-2), ratio: 1 },
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(2), ratio: 1 },
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(4), ratio: 1 },
        ],
    },
    BROKEN_WING_BUTTERFLY: {
        key: 'BROKEN_WING_BUTTERFLY',
        name: 'Broken Wing Butterfly',
        bias: 'SPECIAL',
        construction: 'Butterfly with one open wing',
        context: 'Directional drift, cheap or credit entry',
        /*
         * A call butterfly whose far wing is pushed out one extra step: the lower
         * wing spans two steps (0 -> 2) and the upper spans three (2 -> 5). The
         * UNEQUAL widths are the whole point — "broken" is the asymmetry, not a
         * missing leg, and an equal-width version is just a butterfly.
         *
         * Widening the far wing cheapens the structure, often into a net credit,
         * and buys that discount with real risk on the broken side: the loss
         * there is the difference in wing widths, one step, rather than zero.
         * Leg count and BUY/SELL counts therefore do NOT balance the way they do
         * in a condor, and a test asserting symmetry must exclude this one.
         */
        legs: [
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(2), ratio: 2 },
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(5), ratio: 1 },
        ],
    },
    CALL_RATIO_SPREAD: {
        key: 'CALL_RATIO_SPREAD',
        name: 'Call Ratio Spread',
        bias: 'SPECIAL',
        construction: 'Buy 1 CE, sell 2 higher',
        context: 'Mild rally into resistance',
        /*
         * THE ONLY TEMPLATE HERE WITH UNBOUNDED RISK, and the reason the ratio
         * field exists at all.
         *
         * Two short calls against one long: above the short strike the extra
         * short is NAKED and the loss has no ceiling. It wins on a drift up into
         * the short strike and loses without limit on a gap through it.
         *
         * This is exactly the shape ROADMAP 5.9's leg ordering protects — the
         * long call must be placed BEFORE the two shorts, so a rejected long is
         * discovered while there is nothing to unwind rather than after the naked
         * side is already in the market.
         */
        legs: [
            { side: 'BUY', optionType: 'CE', strikeCriteria: atm(0), ratio: 1 },
            { side: 'SELL', optionType: 'CE', strikeCriteria: atm(2), ratio: 2 },
        ],
    },
};
/**
 * The template for a key, or `undefined` when there is no such strategy.
 *
 * `Object.hasOwn` rather than a bare `STRATEGY_TEMPLATES[key]`, because the
 * catalogue is a plain object and a bare index reaches its PROTOTYPE: with
 * the record typed `Record<string, StrategyTemplate>`, `templateFor('toString')`
 * would hand back `Object.prototype.toString` — a function — while TypeScript
 * insisted it was a StrategyTemplate. Keys arrive from URLs, saved deployment
 * rows and stale tabs, so that is reachable input rather than a curiosity.
 *
 * It returns `undefined` rather than throwing, and never substitutes a
 * default: a mistyped key must not quietly become a short straddle.
 */
export function templateFor(key) {
    return Object.hasOwn(STRATEGY_TEMPLATES, key) ? STRATEGY_TEMPLATES[key] : undefined;
}
//# sourceMappingURL=strategyCatalogue.js.map